import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux'
import { Form, Field } from 'react-final-form';
import { Link } from "react-router-dom";
import { Row, Col, ButtonGroup, Button, Card, CardHeader, CardBody, CardTitle, CardText } from 'reactstrap';
import { FormGroup, Label } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import { Group, User } from '../../../models/';
import { Utils, isSuperAdmin } from '../../../services/';
import { loadStudents, loadTeachers, loadSubjects, removeStudentFromGroup, removeClassroomFromGroup } from '../../../redux/modules';
import * as store from '../../../redux/';
import {
    Role, DaysField, SubjectsField,
    StudentsField, StudentListItem,
    ClassroomListItem, TeachersField
} from '../../../components';
import Icon from '@mdi/react';
import {
    mdiLoading,
    mdiInformationSlabCircleOutline,
    mdiAccountSchoolOutline,
    mdiCalendarMonthOutline,
    mdiAccountPlusOutline,
    mdiAccountArrowRightOutline,
    mdiHumanMaleBoard,
    mdiCardAccountPhoneOutline,
    mdiCommentAccountOutline,
    mdiTrashCanOutline,
    mdiCastEducation,
    mdiTimerSand,
    mdiTimerSandComplete
} from '@mdi/js';

import { DateTimePicker } from 'react-widgets';
import _ from 'lodash';

import './GroupForm.css';

const GroupForm = props => {
    const { t } = useTranslation();

    const { onSubmit, isCreation } = props;
    const { group, isLoadingOne }      = useSelector(state => state.groups);

    const renderDateTimePicker    = ({ input: { onChange, value }, showTime }) =>
        <DateTimePicker
            onChange={onChange}
            format="DD MMM YYYY"
            time={showTime}
            value={!value ? null : new Date(value)}
        />

    const { students } = useSelector(state => state.students);
    const { subjects } = useSelector(state => state.subjects);
    const { teachers } = useSelector(state => state.teachers);

    const dispatch = useDispatch();
    useEffect(() => {
        dispatch(loadStudents());
        dispatch(loadSubjects());
        dispatch(loadTeachers());
    }, [group]);

    const removeStudent = (form, student) => {
        dispatch(removeStudentFromGroup(group, student));
    };

    const removeClassroom = (form, classroom) => {
        dispatch(removeClassroomFromGroup(group, classroom));
    };

    const students_to_add = React.useMemo(() => (students || [])
        .filter(student => !_.some(group?.students || [], { id: student.id }))
        .filter(student => !student.group?.id)
        .sort(s => s.firstname + ' ' + s.lastname), [students, group]);

    return (
        <Form
            initialValues={ group }
            onSubmit={ values => { onSubmit(values); }}
            render={({ handleSubmit, pristine, form, submitting }) => (
                <form className="form" onSubmit={handleSubmit}>
                    <Row className="main row-flex">
                        <Col sm={12} lg={6}>
                            <Card color="light" className="h-100">
                                <CardHeader>
                                    <CardTitle tag="h3">
                                        <Icon path={mdiInformationSlabCircleOutline} size={1.5} />
                                        {t('groups.informations')}
                                    </CardTitle>
                                </CardHeader>
                                <CardBody>
                                    <CardText tag="div">
                                        <FormGroup row>
                                            <Label for="name" sm={3}>{t('groups.name')}</Label>
                                            <Col sm={9}>
                                                <Field required component="input" className="form-control" type="text" name="name" id="name" autoComplete="given-name" placeholder={t('groups.name_placeholder')} />
                                            </Col>
                                        </FormGroup>
                                        <FormGroup row>
                                            <Label for="min_age" sm={3}>{t('groups.min_age')}</Label>
                                            <Col sm={9}>
                                                <Field required component="input" className="form-control" type="number" stpe="1" min="4" max="99" name="min_age" id="min_age" placeholder={t('groups.min_age_placeholder')} />
                                            </Col>
                                        </FormGroup>
                                        <FormGroup row>
                                            <Label for="max_age" sm={3}>{t('groups.max_age')}</Label>
                                            <Col sm={9}>
                                                <Field required component="input" className="form-control" type="number" stpe="1" max="4" max="99" name="max_age" id="max_age" placeholder={t('groups.min_age_placeholder')} />
                                            </Col>
                                        </FormGroup>
                                    </CardText>
                                </CardBody>
                            </Card>
                            <div className="d-block d-lg-none d-xl-none d-xxl-none"><br/></div>
                        </Col>
                        <Col sm={12} lg={6}>
                            <Card color="light" className="h-100">
                                <CardHeader>
                                    <CardTitle tag="h3">
                                        <Icon path={mdiCommentAccountOutline} size={1.5} />
                                        {t('groups.other_informations')}
                                    </CardTitle>
                                </CardHeader>
                                <CardBody>
                                    <CardText tag="div">
                                        <FormGroup row>
                                            <Label for="remarks" sm={3}>{t('groups.remarks')}</Label>
                                            <Col sm={9}>
                                                <Field component="textarea" rows={ 6 } className="form-control" name="remarks" id="remarks" />
                                            </Col>
                                        </FormGroup>
                                    </CardText>
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                    { !isCreation && (
                    <Row className="main row-flex">
                        <Col sm={12} lg={12}>
                            <Card color="light" className="h-100">
                                <CardHeader>
                                    <CardTitle tag="h3">
                                        <Icon path={mdiCastEducation} size={1.5} />&nbsp;
                                        {t('groups.classrooms')}
                                    </CardTitle>
                                </CardHeader>
                                <CardBody>
                                    <CardText tag="div">
                                        <Row>
                                            <Col>
                                                <Card color="primary" className="text-light">
                                                    <CardHeader>
                                                        <Row className="mb-0">
                                                            <Col className="col-auto d-flex flex-column align-items-left justify-content-center text-center">
                                                                <CardTitle tag="h4">
                                                                    { t('groups.add_a_classroom') }&nbsp;&nbsp;&nbsp;&nbsp;
                                                                </CardTitle>
                                                            </Col>
                                                            <Col className="d-flex flex-column align-items-center justify-content-center text-center">
                                                                <h5>{ t('groups.of_subject') }</h5>
                                                                <SubjectsField single name="new_classroom[subject_id]" subjects={ subjects } />
                                                            </Col>
                                                            <Col className="d-flex flex-column align-items-center justify-content-center text-center">
                                                                <h5>{ t('groups.with_teacher') }</h5>
                                                                <TeachersField single name="new_classroom[teacher_id]" teachers={ teachers } />
                                                            </Col>
                                                            <Col className="d-flex flex-column align-items-center justify-content-center text-center">
                                                                <h5>{ t('groups.all_week_on') }</h5>
                                                                <DaysField single name="new_classroom[week_day]" />
                                                            </Col>
                                                            <Col>
                                                                <Row className="mb-0">
                                                                    <Col className="d-flex flex-column align-items-center justify-content-center text-center">
                                                                        <h5>{ t('groups.from') }</h5>
                                                                        <Field className="form-control w-auto" name="new_classroom[start_time]" component="input" type="time" min="08:00" max="22:00" />
                                                                    </Col>
                                                                    <Col className="d-flex flex-column align-items-center justify-content-center text-center">
                                                                        <h5>{ t('groups.to') }</h5>
                                                                        <Field className="form-control w-auto" name="new_classroom[end_time]" component="input" type="time" min="08:00" max="22:00" />
                                                                    </Col>
                                                                </Row>
                                                            </Col>
                                                            <Col className="d-flex align-items-end justify-content-end">
                                                                <Button type="submit" full color="success" disabled={pristine || submitting || isLoadingOne}>
                                                                    <span>
                                                                        <Icon path={isLoadingOne ? mdiLoading : mdiAccountPlusOutline} spin={isLoadingOne ? 1 : 0} size={1} />&nbsp;
                                                                        {t("common.add")}
                                                                    </span>
                                                                </Button>
                                                            </Col>
                                                        </Row>
                                                    </CardHeader>
                                                </Card>
                                            </Col>
                                        </Row>
                                        <Row>
                                            { _.sortBy(group?.classrooms, 'start_time').map(classroom => (
                                            <Col sm="12" md="12">
                                                <ClassroomListItem
                                                    classroom={ classroom }
                                                    className="mb-1"
                                                    actions={(
                                                        <>
                                                            <Button color="danger" size="sm" onClick={() => removeClassroom(group, classroom)}>
                                                                <Icon path={mdiTrashCanOutline} size={1} />
                                                            </Button>
                                                        </>
                                                    )}
                                                />
                                            </Col>
                                            ))}
                                        </Row>
                                    </CardText>
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                    )}

                    { !isCreation && (
                    <Row className="main row-flex">
                        <Col sm={12} lg={12}>
                            <Card color="light" className="h-100">
                                <CardHeader>
                                    <CardTitle tag="h3">
                                        <Icon path={mdiAccountSchoolOutline} size={1.5} />
                                        {t('groups.students')}
                                    </CardTitle>
                                </CardHeader>
                                <CardBody>
                                    <CardText tag="div">
                                        <Row>
                                            <Col>
                                                <Card color="primary" className="text-light">
                                                    <CardHeader>
                                                        <Row className="mb-0">
                                                            <Col className="col-auto">
                                                                <CardTitle tag="h4">
                                                                    { t('groups.add_a_student') }
                                                                </CardTitle>
                                                            </Col>
                                                            <Col>
                                                                <CardText>
                                                                    <StudentsField multiple name="new_students[]" students={ students_to_add } />
                                                                </CardText>
                                                            </Col>
                                                            <Col className="col-auto">
                                                                <Button type="submit" color="success" disabled={pristine || submitting || isLoadingOne}>
                                                                    <span>
                                                                        <Icon path={isLoadingOne ? mdiLoading : mdiAccountPlusOutline} spin={isLoadingOne ? 1 : 0} size={1} />&nbsp;
                                                                        {t("common.add")}
                                                                    </span>
                                                                </Button>
                                                            </Col>
                                                        </Row>
                                                    </CardHeader>
                                                </Card>
                                            </Col>
                                        </Row>
                                        <Row>
                                            { group?.students?.map(student => (
                                            <Col sm="12" md="6">
                                                <StudentListItem
                                                    student={ student }
                                                    className="mb-1"
                                                    actions={(
                                                        <>
                                                            <Button tag={Link} to={ `/students/show/${ student.id }` } color="warning" size="sm">
                                                                <Icon path={mdiAccountArrowRightOutline} size={1} />
                                                            </Button>&nbsp;
                                                            <Button color="danger" size="sm" onClick={() => removeStudent(form, student)}>
                                                                <Icon path={mdiTrashCanOutline} size={1} />
                                                            </Button>
                                                        </>
                                                    )}
                                                />
                                            </Col>
                                            ))}
                                        </Row>
                                    </CardText>
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                    )}

                    <Row className="main">
                        <Col className="text-center">
                            <ButtonGroup>
                                <Button tag={Link} to={ -1 } color="warning">
                                    <span><i className="fa fa-arrow-left"></i>&nbsp;{t('common.back')}</span>
                                </Button>
                                <Button type="submit" color="success" disabled={pristine || submitting || isLoadingOne}>
                                    <span>
                                        <i className={`fa ${ isLoadingOne ? 'fa-spinner fa-spin' : 'fa fa-save'}`}></i>&nbsp;{t("common.save")}
                                    </span>
                                </Button>
                            </ButtonGroup>
                        </Col>
                    </Row>
                </form>
        )} />
    );
}

export default GroupForm;
